import React from 'react';
import { useDarkTheme } from '../../contexts/DarkThemeContext';
import './DiagramTooltip.css';

// Smart positioning function to ensure tooltip stays within viewport
const calculateTooltipPosition = (mouseX: number, mouseY: number, tooltipWidth: number = 300, tooltipHeight: number = 200) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const padding = 20; // Minimum distance from viewport edges
  const mouseOffset = 15; // Distance from mouse cursor
  
  let x = mouseX + mouseOffset; // Default offset from mouse
  let y = mouseY - mouseOffset;
  
  // Adjust horizontal position if tooltip would go off-screen
  if (x + tooltipWidth + padding > viewportWidth) {
    x = mouseX - tooltipWidth - mouseOffset; // Position to the left of mouse
  }
  
  // Ensure tooltip doesn't go off the left edge
  if (x < padding) {
    x = padding;
  }
  
  // Adjust vertical position if tooltip would go off-screen
  if (y + tooltipHeight + padding > viewportHeight) {
    y = mouseY - tooltipHeight - mouseOffset; // Position above mouse
  }
  
  // Ensure tooltip doesn't go off the top edge
  if (y < padding) {
    y = padding;
  }
  
  // Final boundary check to ensure tooltip is always fully visible
  x = Math.max(padding, Math.min(x, viewportWidth - tooltipWidth - padding));
  y = Math.max(padding, Math.min(y, viewportHeight - tooltipHeight - padding));
  
  return { x, y };
};

// Color constants
const LIGHT_COLORS = {
  WHITE: '#ffffff',
  TEXT_PRIMARY: '#2C3E50',
  TEXT_SECONDARY: '#555',
  PRIMARY: '#007bff',
  PRIMARY_HOVER: '#0056b3',
  API_INTERNAL: '#28a745',
  API_EXTERNAL: '#17a2b8',
  DROP_SHADOW_MEDIUM: 'rgba(0,0,0,0.15)',
  BORDER: '#ccc',
  SEPARATOR: '#eee',
} as const;

const DARK_COLORS = {
  WHITE: '#142737',
  TEXT_PRIMARY: '#fff',
  TEXT_SECONDARY: 'rgba(255,255,255,0.7)',
  PRIMARY: '#007aaa',
  PRIMARY_HOVER: '#28b1ec',
  API_INTERNAL: '#099a49',
  API_EXTERNAL: '#28b1ec',
  DROP_SHADOW_MEDIUM: 'rgba(0,0,0,0.4)',
  BORDER: 'rgba(255,255,255,0.15)',
  SEPARATOR: 'rgba(255,255,255,0.1)',
} as const;

const getColors = (isDark: boolean) => isDark ? DARK_COLORS : LIGHT_COLORS;

// TypeScript interfaces
interface ApiData {
  id?: string;
  name: string;
  type?: string;
  description?: string;
  developerProgram?: boolean;
  latestApiVersionNumber?: string;
  children?: ApiData[];
}

interface TooltipData {
  x: number;
  y: number;
  apiData: ApiData;
}

interface DiagramTooltipProps {
  tooltip: TooltipData | null;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onViewDetails: (apiId: string) => void;
}

const DiagramTooltip: React.FC<DiagramTooltipProps> = ({
  tooltip,
  onMouseEnter,
  onMouseLeave,
  onViewDetails
}) => {
  const { isDarkTheme } = useDarkTheme();
  const COLORS = getColors(isDarkTheme);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  
  React.useEffect(() => {
    if (tooltip && tooltipRef.current) {
      // Get actual tooltip dimensions after rendering
      const rect = tooltipRef.current.getBoundingClientRect();
      const optimalPosition = calculateTooltipPosition(
        tooltip.x, 
        tooltip.y, 
        rect.width || 300, 
        rect.height || 200
      );
      setPosition(optimalPosition);
    }
  }, [tooltip]);
  
  if (!tooltip) return null;

  const isDetailedTooltip = tooltip.apiData.type === 'api' || 
                           tooltip.apiData.description || 
                           tooltip.apiData.developerProgram !== undefined || 
                           tooltip.apiData.latestApiVersionNumber;

  return (
    <div
      ref={tooltipRef}
      className="diagram-tooltip animate-in"
      style={{
        position: 'fixed', // Changed from absolute to fixed for better viewport positioning
        left: `${position.x}px`,
        top: `${position.y}px`,
        backgroundColor: COLORS.WHITE,
        border: `1px solid ${COLORS.BORDER}`,
        borderRadius: '8px',
        padding: isDetailedTooltip ? '12px' : '8px',
        boxShadow: `0 4px 12px ${COLORS.DROP_SHADOW_MEDIUM}`,
        zIndex: 10000, // Increased z-index to ensure it's always on top
        maxWidth: '300px',
        fontSize: '13px',
        fontFamily: 'Arial, sans-serif',
        lineHeight: '1.4',
        pointerEvents: 'auto', // Ensure tooltip can receive mouse events
        // Add a subtle animation for smooth appearance
        opacity: position.x === 0 && position.y === 0 ? 0 : 1,
        transition: 'opacity 0.2s ease-in-out'
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isDetailedTooltip ? (
        <DetailedTooltipContent 
          apiData={tooltip.apiData} 
          onViewDetails={onViewDetails}
        />
      ) : (
        <SimpleTooltipContent apiData={tooltip.apiData} />
      )}
    </div>
  );
};

// Detailed tooltip for API nodes
const DetailedTooltipContent: React.FC<{
  apiData: ApiData;
  onViewDetails: (apiId: string) => void;
}> = ({ apiData, onViewDetails }) => {
  const { isDarkTheme } = useDarkTheme();
  const COLORS = getColors(isDarkTheme);
  return (
    <>
      <div style={{ marginBottom: '8px' }}>
        <strong style={{ color: COLORS.TEXT_PRIMARY, fontSize: '14px' }}>
          {apiData.name}
        </strong>
      </div>
      
      {apiData.description && (
        <div style={{ marginBottom: '8px', color: COLORS.TEXT_SECONDARY }}>
          <strong>Description:</strong> {apiData.description.length > 350 
            ? apiData.description.substring(0, 350) + '...' 
            : apiData.description}
        </div>
      )}
      
      {apiData.developerProgram !== undefined && (
        <div style={{ marginBottom: '8px', color: COLORS.TEXT_SECONDARY }}>
          <strong>Developer Program:</strong>{' '}
          <span style={{ 
            color: apiData.developerProgram ? COLORS.API_EXTERNAL : COLORS.API_INTERNAL,
            fontWeight: 'bold',
          }}>
            {apiData.developerProgram ? 'Yes' : 'No'}
          </span>
        </div>
      )}
      
      {apiData.latestApiVersionNumber && (
        <div style={{ marginBottom: '8px', color: COLORS.TEXT_SECONDARY }}>
          <strong>Version:</strong> {apiData.latestApiVersionNumber}
        </div>
      )}
      
      <div style={{ 
        borderTop: `1px solid ${COLORS.SEPARATOR}`, 
        paddingTop: '8px',
        marginTop: '8px'
      }}>
        <ViewDetailsButton 
          apiId={apiData.id!} 
          onViewDetails={onViewDetails}
        />
      </div>
    </>
  );
};

// Simple tooltip for long node names
const SimpleTooltipContent: React.FC<{ apiData: ApiData }> = ({ apiData }) => {
  const { isDarkTheme } = useDarkTheme();
  const COLORS = getColors(isDarkTheme);
  return (
    <div style={{ color: COLORS.TEXT_PRIMARY, fontSize: '13px', fontWeight: '500' }}>
      {apiData.name}
    </div>
  );
};

// View Details Button with hover effects
const ViewDetailsButton: React.FC<{
  apiId: string;
  onViewDetails: (apiId: string) => void;
}> = ({ apiId, onViewDetails }) => {
  const { isDarkTheme } = useDarkTheme();
  const COLORS = getColors(isDarkTheme);
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <button
      onClick={() => onViewDetails(apiId)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: isHovered ? COLORS.PRIMARY_HOVER : COLORS.PRIMARY,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        padding: '6px 12px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 'bold',
        width: '100%',
        transition: 'background-color 0.2s'
      }}
    >
      View API Details →
    </button>
  );
};

export default DiagramTooltip;
export type { TooltipData, ApiData };
